import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { CalendarIcon, ClockIcon, TagIcon, UserIcon } from 'lucide-react';
export const ProfilePage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState('posts');
  const variants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  // Profile data
  const profile = {
    id: 1,
    name: 'Azn Alahah',
    title: 'Founder & Executive Director',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pHWegwvKPXZhGe1ZwtEHkB/Generated_Image_September_17%2C_2025_-_8_14PM~2.png',
    bio: 'Azn Alahah is a microbiome scientist with over 5 years of experience in pharmaceutical research and wellness science. He founded CalMiR to bridge the gap between academic research and real-world health applications. His work on gut-brain axis communication has been published in scientific journals and has helped advance our understanding of how the microbiome influences overall health.',
    education: ['Ph.D. in Microbiology, Stanford University', 'M.S. in Biochemistry, MIT', 'B.S. in Biology, UC Berkeley'],
    expertise: ['Microbiome Research', 'Therapeutic Development', 'Clinical Trials', 'Gut Health'],
    socialMedia: {
      twitter: 'https://twitter.com/aznalahah',
      linkedin: 'https://linkedin.com/in/aznalahah',
      researchGate: 'https://researchgate.net/profile/Azn_Alahah'
    }
  };
  // Posts data
  const posts = [{
    id: 1,
    title: 'The Microbiome-Gut-Brain Axis: New Discoveries',
    type: 'Article',
    date: 'June 15, 2023',
    image: 'https://images.unsplash.com/photo-1559757175-7cb036bd7d57?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Our latest research reveals new pathways of communication between gut bacteria and neural networks.',
    topics: ['Microbiome', 'Neuroscience'],
    link: '/article/microbiome-gut-brain'
  }, {
    id: 2,
    title: 'Probiotics: Beyond the Basics',
    type: 'Video',
    date: 'May 3, 2023',
    image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'An in-depth look at how specific probiotic strains affect different body systems.',
    topics: ['Probiotics', 'Clinical Applications'],
    duration: '42:18',
    link: '/video/probiotics-beyond-basics'
  }, {
    id: 3,
    title: 'Microbiome Testing Methods: A Comparative Analysis',
    type: 'Research Paper',
    date: 'April 20, 2023',
    image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Evaluating the accuracy and clinical relevance of different microbiome testing technologies.',
    topics: ['Research Methods', 'Diagnostics'],
    link: '/article/microbiome-testing-methods'
  }, {
    id: 4,
    title: 'Diet, Inflammation, and the Microbiome',
    type: 'Podcast',
    date: 'March 8, 2023',
    image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'How specific dietary patterns influence inflammatory pathways through microbiome modulation.',
    topics: ['Nutrition', 'Inflammation'],
    duration: '56:42',
    link: '/podcast/diet-inflammation-microbiome'
  }, {
    id: 5,
    title: 'Biofilm Disruption: New Therapeutic Approaches',
    type: 'Article',
    date: 'February 15, 2023',
    image: 'https://images.unsplash.com/photo-1581093458791-9a9cd7093ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Exploring novel compounds that effectively disrupt pathogenic biofilms while preserving beneficial bacteria.',
    topics: ['Biofilms', 'Therapeutics'],
    link: '/article/biofilm-disruption'
  }];
  // Research papers data
  const researchPapers = [{
    id: 1,
    title: 'Bacterial Metabolites Mediate Vagus Nerve Activation in Gut-Brain Communication',
    journal: 'Nature Microbiology',
    year: '2023',
    citation: 'Alahah, A., et al. (2023). Bacterial Metabolites Mediate Vagus Nerve Activation in Gut-Brain Communication. Nature Microbiology, 8(3), 456-470.',
    link: 'https://doi.org/10.1038/s41564-023-1234-5'
  }, {
    id: 2,
    title: 'Comparative Analysis of Commercial Probiotic Formulations: Viability and Strain-Specific Effects',
    journal: 'Journal of Functional Foods',
    year: '2022',
    citation: 'Alahah, A., Rodriguez, M., & Patel, M. (2022). Comparative Analysis of Commercial Probiotic Formulations: Viability and Strain-Specific Effects. Journal of Functional Foods, 89, 104932.',
    link: 'https://doi.org/10.1016/j.jff.2022.104932'
  }, {
    id: 3,
    title: 'Microbiome Signatures in Inflammatory Bowel Disease: Implications for Personalized Treatment',
    journal: 'Gastroenterology',
    year: '2021',
    citation: 'Nkosi, A., Alahah, A., & Park, L. (2021). Microbiome Signatures in Inflammatory Bowel Disease: Implications for Personalized Treatment. Gastroenterology, 160(7), 2346-2360.',
    link: 'https://doi.org/10.1053/j.gastro.2021.02.035'
  }, {
    id: 4,
    title: 'Novel Biofilm-Disrupting Compounds Derived from Plant Polyphenols',
    journal: 'Antimicrobial Agents and Chemotherapy',
    year: '2020',
    citation: 'Alahah, A., Kim, D., & Chang, R. (2020). Novel Biofilm-Disrupting Compounds Derived from Plant Polyphenols. Antimicrobial Agents and Chemotherapy, 64(5), e02145-19.',
    link: 'https://doi.org/10.1128/AAC.02145-19'
  }];
  return <div className="bg-white w-full" data-id="element-1614" data-mp="23hf52ztqbmo4">
      {/* Hero Section with Profile */}
      <section className="relative pt-32 pb-16 bg-gray-50" data-id="element-1615" data-mp="1yjdmsh44nrdp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1616" data-mp="2d7gqtkym0szv">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} transition={{
          duration: 0.8
        }} className="flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-1617" data-mp="2d91m5csr0fys">
            <div className="w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden shadow-lg" data-id="element-1618" data-mp="2w5ko9i54x3lu">
              <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" data-id="element-1619" data-mp="2r6q4acvfl905" />
            </div>
            <div className="flex-1" data-id="element-1620" data-mp="11tw85cn62uzv">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1621" data-mp="wt8e0i3n5y1f">
                {profile.name}
              </h1>
              <p className="text-xl text-blue-600 mb-4" data-id="element-1622" data-mp="38hd8w1ouvlcj">{profile.title}</p>
              <p className="text-gray-700 mb-6" data-id="element-1623" data-mp="3dfesqou3vop2">{profile.bio}</p>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-1624" data-mp="3dgzmwdcid5me">
                {profile.expertise.map((skill, index) => <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium" data-id="element-1625" data-mp="33p2c37pewfo7" data-mpi={index}>
                    {skill}
                  </span>)}
              </div>
              <div className="flex gap-4" data-id="element-1626" data-mp="2umkoopy0tgzm">
                <a href={profile.socialMedia.twitter} className="text-gray-500 hover:text-blue-600" target="_blank" rel="noopener noreferrer" data-id="element-1627" data-mp="2fs6d3agsd9uz">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1628" data-mp="e0jgmte4kmr4">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" data-id="element-1629" data-mp="32um0dnge0go3" />
                  </svg>
                </a>
                <a href={profile.socialMedia.linkedin} className="text-gray-500 hover:text-blue-600" target="_blank" rel="noopener noreferrer" data-id="element-1630" data-mp="2f3zxsbp5jolh">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1631" data-mp="8f3dukinwzwf">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" data-id="element-1632" data-mp="azj8ki05b1jq" />
                  </svg>
                </a>
                <a href={profile.socialMedia.researchGate} className="text-gray-500 hover:text-blue-600" target="_blank" rel="noopener noreferrer" data-id="element-1633" data-mp="3o62wsgqdkryn">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1634" data-mp="1xth5c69k2ly">
                    <path d="M19.586 0c-2.648 0-4.9.814-6.703 2.666-1.805 1.85-2.707 4.114-2.707 6.783 0 2.502.944 4.632 2.846 6.318 1.896 1.69 4.2 2.502 6.957 2.502.26 0 .52-.01.773-.026v-3.322c-.26.014-.517.02-.773.02-1.518 0-2.81-.477-3.877-1.425-1.073-.955-1.61-2.13-1.61-3.54 0-1.348.517-2.514 1.553-3.51 1.03-.994 2.293-1.492 3.795-1.492 1.533 0 2.82.501 3.863 1.495 1.04.997 1.56 2.195 1.56 3.585 0 1.12-.297 2.08-.89 2.88-.592.8-1.164 1.202-1.72 1.202-.304 0-.498-.112-.581-.337-.084-.226-.123-.57-.123-1.03v-4.637h-1.963v.731h-.033c-.084-.226-.293-.435-.63-.626-.338-.19-.734-.285-1.188-.285-.745 0-1.326.255-1.746.768-.42.51-.63 1.24-.63 2.189 0 .882.21 1.562.63 2.048.42.49.995.733 1.72.733.507 0 .942-.112 1.304-.336.362-.224.63-.501.807-.836h.033v.255c0 .582.151 1.03.454 1.347.304.314.713.475 1.227.475.994 0 1.92-.478 2.778-1.425.859-.955 1.289-2.056 1.289-3.305 0-1.728-.636-3.175-1.905-4.34-1.27-1.164-2.887-1.75-4.848-1.75zm-9.925 12.817c-1.647 0-2.99.516-4.017 1.545-1.033 1.033-1.542 2.402-1.542 4.112 0 1.71.509 3.081 1.542 4.115 1.027 1.028 2.37 1.542 4.017 1.542 1.648 0 2.991-.514 4.017-1.542 1.032-1.034 1.542-2.405 1.542-4.115 0-1.71-.51-3.079-1.542-4.112-1.026-1.029-2.369-1.545-4.017-1.545zm0 1.544c1.312 0 2.405.41 3.27 1.227.87.817 1.304 1.892 1.304 3.228 0 1.342-.434 2.42-1.304 3.23-.865.81-1.958 1.22-3.27 1.22-1.313 0-2.397-.41-3.255-1.22-.859-.81-1.289-1.888-1.289-3.23 0-1.336.43-2.411 1.289-3.228.858-.817 1.942-1.226 3.255-1.226zm-1.304 1.904c-.289.16-.577.483-.867.97-.29.485-.433 1.022-.433 1.607 0 .582.143 1.106.433 1.572.289.466.577.784.867.955v-5.104z" data-id="element-1635" data-mp="1dcyq2xwzmxe0" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Education & Background */}
      <section className="py-10 bg-white" data-id="element-1636" data-mp="3ljn1444853mn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1637" data-mp="3lkoggks9wca6">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="bg-blue-50 rounded-lg p-6 shadow-sm" data-id="element-1638" data-mp="3glbqhr63up3d">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1639" data-mp="398vr5nxlx6d5">
              Education & Background
            </h2>
            <ul className="space-y-2" data-id="element-1640" data-mp="3e1owoe0wgiqa">
              {profile.education.map((edu, index) => <li key={index} className="flex items-center" data-id="element-1641" data-mp="2zdavs8n1n4cd" data-mpi={index}>
                  <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3" data-id="element-1642" data-mp="349syz42q6uml">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" data-id="element-1643" data-mp="3s0y9r4tt6wq1">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-1644" data-mp="3iyfdj3kj3icr" />
                    </svg>
                  </div>
                  <span className="text-gray-700" data-id="element-1645" data-mp="2bn82xqbb1ucp">{edu}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>
      {/* Content Tabs */}
      <section className="py-10 bg-gray-50" data-id="element-1646" data-mp="1z31evugzzjqw">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1647" data-mp="2e0ad984pxns4">
          <div className="border-b border-gray-200 mb-8" data-id="element-1648" data-mp="2dyhc28cvjvri">
            <nav className="-mb-px flex space-x-8" data-id="element-1649" data-mp="292ilce42q1nx">
              <button onClick={() => setActiveTab('posts')} className={`${activeTab === 'posts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm sm:text-base`} data-id="element-1650" data-mp="28zmhk95joirk">
                Recent Publications
              </button>
              <button onClick={() => setActiveTab('research')} className={`${activeTab === 'research' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm sm:text-base`} data-id="element-1651" data-mp="1q94uol7e8he4">
                Research Papers
              </button>
            </nav>
          </div>
          {activeTab === 'posts' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-1652" data-mp="9utpmcgf6u8y">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1653" data-mp="ok9bpf50abib">
                {posts.map(post => <motion.div key={post.id} initial="hidden" animate="visible" variants={variants} transition={{
              duration: 0.5,
              delay: post.id * 0.1
            }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-1654" data-mp="sn60jnl533up">
                    <Link to={post.link} className="block" data-id="element-1655" data-mp="2n2bhtxyzwsa5">
                      <img src={post.image} alt={post.title} className="w-full h-48 object-cover" data-id="element-1656" data-mp="2n0qno9glfbct" />
                      <div className="p-6" data-id="element-1657" data-mp="2d5q9b2nroz1k">
                        <div className="flex items-center mb-2" data-id="element-1658" data-mp="3arw34nimg1ml">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1659" data-mp="3ar4ejwnfz45t">
                            {post.type}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1660" data-mp="2wsny3dzs1zah">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4" data-id="element-1661" data-mp="1pfvxmof6pgiw">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-gray-500 text-xs" data-id="element-1662" data-mp="1mu4xkmolxz2d">
                          <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1663" data-mp="1msae7cszif0e" />
                          <span className="mr-3" data-id="element-1664" data-mp="1rspotwlvt8eo">{post.date}</span>
                          {post.duration && <>
                              <ClockIcon className="h-4 w-4 mr-1" data-id="element-1665" data-mp="1hus25djo7jo6" />
                              <span data-id="element-1666" data-mp="1ht7apwsnkguj">{post.duration}</span>
                            </>}
                        </div>
                      </div>
                    </Link>
                  </motion.div>)}
              </div>
            </motion.div>}
          {activeTab === 'research' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5
        }} className="space-y-6" data-id="element-1667" data-mp="z40walyw6bej">
              {researchPapers.map(paper => <motion.div key={paper.id} initial="hidden" animate="visible" variants={variants} transition={{
            duration: 0.5,
            delay: paper.id * 0.1
          }} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-1668" data-mp="6im198xgy6lw">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1669" data-mp="peln01zusdx1">
                    {paper.title}
                  </h3>
                  <p className="text-blue-600 mb-2" data-id="element-1670" data-mp="y9rxhea2jk5w">
                    {paper.journal} • {paper.year}
                  </p>
                  <p className="text-gray-700 text-sm mb-4" data-id="element-1671" data-mp="u8y62axf3n89">{paper.citation}</p>
                  <a href={paper.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-1672" data-mp="186muttulveil">
                    View Publication
                    <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1673" data-mp="2elui9zi2uffp">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-1674" data-mp="1qwt8a7tliw8b" />
                    </svg>
                  </a>
                </motion.div>)}
            </motion.div>}
        </div>
      </section>
    </div>;
};